import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import {
  getIsEnterpriseSession,
  getPersistedSalonDisplayName,
  getTenantConfigFromSession,
  getTenantIdFromSession,
  mergeSalonNameIntoSession,
  subscribeTenantSalonDisplay,
} from '@/lib/tenant/session';

import { QueryKeys } from '@/constants/query-keys';

/**
 * Single source of truth for the authed user.
 * Uses getSession() — reads localStorage only, zero network requests.
 * The middleware already validates the JWT server-side on every navigation.
 * onAuthStateChange in AuthSync (providers.tsx) pushes TOKEN_REFRESHED into this cache.
 */
function useAuthUserQuery() {
  // Starts false on both server and first client paint (avoids a hydration
  // mismatch), then flips once mounted if a tenant config is present.
  const [hasTenantConfig, setHasTenantConfig] = useState(false);

  useEffect(() => {
    setHasTenantConfig(!!getTenantConfigFromSession()?.supabase_url);
  }, []);

  return useQuery({
    queryKey: [QueryKeys.USER],
    queryFn: async () => {
      const supabase = createSupabaseBrowserClient();
      // getSession() reads from localStorage — no network call
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.user ?? null;
    },
    enabled: hasTenantConfig,
    staleTime: Infinity, // never auto-stale; TOKEN_REFRESHED event updates cache
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useUser() {
  return useAuthUserQuery();
}

export function useIsAdmin() {
  const q = useAuthUserQuery();
  return {
    ...q,
    // derive from cached user — no second query, no second network call
    data:
      (q.data?.user_metadata?.is_admin ?? false) || getIsEnterpriseSession(),
  };
}

function salonLabelClientSnapshot(): string {
  return getPersistedSalonDisplayName() ?? 'N';
}

/**
 * Salon display name for the sidebar initial (first letter).
 * useSyncExternalStore + explicit same-tab events so React always re-reads localStorage after login.
 * No DB column needed — one letter is derived from the stored display name.
 */
export function useSalonName(): string {
  const label = useSyncExternalStore(
    subscribeTenantSalonDisplay,
    salonLabelClientSnapshot,
    () => 'N',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (getPersistedSalonDisplayName()) return;
    const tenantId = getTenantIdFromSession();
    if (!tenantId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/tenant/info', {
          credentials: 'same-origin',
        });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const name = (json.salon_name as string | null) ?? null;
        if (name && !cancelled) mergeSalonNameIntoSession(name);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return label;
}
